/**
 * Cart Drawer — Native Shopify cart drawer for iS Clinical (Pipeline v7.1.2)
 *
 * Handles: bundled section rendering, quantity ±, remove, selling plan toggle,
 * product recommendations, free shipping bar, auto-open on add, header badge.
 */
(function () {
  'use strict';

  var SECTION_KEY = 'cart-drawer';

  var SELECTORS = {
    drawer: '[data-drawer="drawer-cart"]',
    drawerToggle: '[data-drawer-toggle="drawer-cart"]',
    lineItems: '[data-cart-drawer-line-items]',
    cartForm: '[data-cart-drawer-form]',
    cartEmpty: '[data-cart-drawer-empty]',
    cartBottom: '[data-cart-drawer-bottom]',
    cartSubtotal: '[data-cart-drawer-subtotal]',
    cartTotal: '[data-cart-drawer-total]',
    cartFinal: '[data-cart-drawer-final]',
    shippingBar: '[data-cart-drawer-shipping-bar]',
    removeBtn: '[data-drawer-remove]',
    decreaseBtn: '[data-drawer-decrease]',
    increaseBtn: '[data-drawer-increase]',
    quantityInput: '[data-drawer-qty-input]',
    sellingPlanToggle: '[data-selling-plan-toggle]',
    sellingPlanSelect: '[data-selling-plan-select]',
    recommendations: '[data-cart-recommendations]',
    productIds: '[data-cart-product-ids]',
    headerCartCount: '[data-header-cart-count]',
    headerCartPrice: '[data-header-cart-price]',
    headerCartFull: '[data-header-cart-full]',
    gwpMessages: '[data-gwp-messages]',
  };

  var lastItemCount = null;
  var isRefreshing = false;
  var internalFetch = false; // Marks our own cart API calls so we don't double-refresh

  // Locale-aware root path per Shopify docs
  var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ─── Response handler with error checking ───

  function assertOk(response) {
    if (!response.ok) {
      return response.json().then(function (body) {
        var err = new Error(body.description || body.message || 'Cart error');
        err.status = response.status;
        throw err;
      });
    }
    return response.json();
  }

  // ─── Cart API helpers (locale-aware + bundled section rendering) ───

  function withInternalFlag(promise) {
    internalFetch = true;
    return promise.finally(function () {
      internalFetch = false;
    });
  }

  function fetchCart() {
    return withInternalFlag(fetch(rootPath + 'cart.js').then(assertOk));
  }

  function changeItem(lineKey, quantity) {
    return withInternalFlag(fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
        sections: SECTION_KEY,
        sections_url: '/',
      }),
    }).then(assertOk));
  }

  function changeItemByLine(lineIndex, quantity, sellingPlan) {
    var body = {
      line: lineIndex,
      quantity: quantity,
      sections: SECTION_KEY,
      sections_url: '/',
    };
    if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
    return withInternalFlag(fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(assertOk));
  }

  function addToCart(variantId, quantity) {
    return withInternalFlag(fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }],
        sections: SECTION_KEY,
        sections_url: '/',
      }),
    }).then(assertOk));
  }

  // ─── DOM swap helpers ───

  function swapSectionHtml(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    swapEl(doc, SELECTORS.lineItems, 'innerHTML');
    swapEl(doc, SELECTORS.cartSubtotal, 'innerHTML');
    swapEl(doc, SELECTORS.cartTotal, 'textContent');
    swapEl(doc, SELECTORS.cartFinal, 'textContent');
    swapEl(doc, SELECTORS.shippingBar, 'innerHTML');
  }

  function swapEl(doc, selector, prop) {
    var fresh = doc.querySelector(selector);
    var live = document.querySelector(selector);
    if (fresh && live) live[prop] = fresh[prop];
  }

  // ─── Collect current item keys for enter animation diffing ───

  function getItemKeys() {
    var keys = [];
    var items = document.querySelectorAll('[data-drawer-item]');
    items.forEach(function (el) {
      var input = el.querySelector('[data-drawer-update]');
      if (input) keys.push(input.getAttribute('data-drawer-update'));
    });
    return keys;
  }

  // ─── Apply bundled cart response (change.js returns full cart + sections) ───

  function applyCartUpdate(cart) {
    var prevKeys = getItemKeys();

    var sectionHtml = cart.sections && cart.sections[SECTION_KEY];
    if (sectionHtml) {
      swapSectionHtml(sectionHtml);
    }

    // Mark new items with enter animation
    var newItems = document.querySelectorAll('[data-drawer-item]');
    newItems.forEach(function (el) {
      var input = el.querySelector('[data-drawer-update]');
      if (input) {
        var key = input.getAttribute('data-drawer-update');
        if (prevKeys.indexOf(key) === -1) {
          el.classList.add('cart-drawer__item--entering');
          el.addEventListener('animationend', function onEnd() {
            el.removeEventListener('animationend', onEnd);
            el.classList.remove('cart-drawer__item--entering');
          });
        }
      }
    });

    toggleCartStates(cart);
    updateHeaderBadge(cart);
    lastItemCount = cart.item_count;
    loadRecommendations();

    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: { cart: cart, _cartDrawerOrigin: true },
        bubbles: true,
      })
    );
  }

  // ─── Error handler — removes loading state, logs error ───

  function handleCartError(itemEl, err) {
    if (itemEl) itemEl.classList.remove('cart-drawer__item--loading');
    if (err && err.status === 422) {
      console.warn('[CartDrawer] Stock limit reached:', err.message);
    } else {
      console.error('[CartDrawer] Cart error:', err && err.message || err);
    }
    // Refresh drawer to restore correct state after failed operation
    refreshCartDrawer();
  }

  // ─── Standalone refresh (fallback for GWP re-renders + drawer open) ───

  function refreshCartDrawer() {
    if (isRefreshing) return;
    isRefreshing = true;

    fetchCart()
      .then(function (cart) {
        return fetch('/?section_id=' + SECTION_KEY)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            swapSectionHtml(html);
            toggleCartStates(cart);
            updateHeaderBadge(cart);
            lastItemCount = cart.item_count;
            loadRecommendations();

            document.dispatchEvent(
              new CustomEvent('theme:cart:change', {
                detail: { cart: cart, _cartDrawerOrigin: true },
                bubbles: true,
              })
            );
          });
      })
      .catch(function (err) {
        console.error('[CartDrawer] Refresh failed:', err);
      })
      .finally(function () {
        isRefreshing = false;
      });
  }

  // ─── Toggle empty / filled states ───

  function toggleCartStates(cart) {
    var emptyEl = document.querySelector(SELECTORS.cartEmpty);
    var formEl = document.querySelector(SELECTORS.cartForm);
    var bottomEl = document.querySelector(SELECTORS.cartBottom);

    if (!emptyEl || !formEl || !bottomEl) return;

    if (cart.item_count === 0) {
      emptyEl.style.display = '';
      formEl.style.display = 'none';
      bottomEl.style.display = 'none';
    } else {
      emptyEl.style.display = 'none';
      formEl.style.display = '';
      bottomEl.style.display = '';
    }
  }

  // ─── Header cart badge ───

  function updateHeaderBadge(cart) {
    var countEls = document.querySelectorAll(SELECTORS.headerCartCount);
    var priceEls = document.querySelectorAll(SELECTORS.headerCartPrice);
    var fullEls = document.querySelectorAll(SELECTORS.headerCartFull);

    countEls.forEach(function (el) {
      el.textContent = cart.item_count;
      el.setAttribute('data-header-cart-count', cart.item_count);
    });

    priceEls.forEach(function (el) {
      el.setAttribute('data-header-cart-price', cart.total_price);
    });

    fullEls.forEach(function (el) {
      var isFull = cart.item_count > 0 ? 'true' : 'false';
      el.setAttribute('data-header-cart-full', isFull);
    });
  }

  // ─── Quantity change ───

  function handleQuantityChange(btn, delta) {
    var item = btn.closest('[data-drawer-item]');
    if (!item) return;

    var input = item.querySelector(SELECTORS.quantityInput);
    if (!input) return;

    var lineKey = input.getAttribute('data-drawer-update');
    var currentQty = parseInt(input.value, 10) || 0;
    var newQty = Math.max(0, currentQty + delta);

    item.classList.add('cart-drawer__item--loading');
    input.value = newQty;

    // Optimistically update line total and footer
    var lineTotal = item.querySelector('.cart-drawer__item-line-total');
    if (lineTotal) {
      var unitPrice = parseInt(lineTotal.getAttribute('data-unit-price'), 10);
      if (unitPrice) {
        var priceDiff = delta * unitPrice;
        lineTotal.textContent = formatMoney(unitPrice * newQty);

        var cartTotalEl = document.querySelector(SELECTORS.cartTotal);
        var cartFinalEl = document.querySelector(SELECTORS.cartFinal);
        if (cartTotalEl) {
          var currentTotal = parseMoney(cartTotalEl.textContent);
          cartTotalEl.textContent = formatMoney(currentTotal + priceDiff);
        }
        if (cartFinalEl) {
          var currentFinal = parseMoney(cartFinalEl.textContent);
          cartFinalEl.textContent = formatMoney(currentFinal + priceDiff);
        }
      }
    }

    changeItem(lineKey, newQty)
      .then(applyCartUpdate)
      .catch(function (err) { handleCartError(item, err); });
  }

  function handleQuantityInput(input) {
    var item = input.closest('[data-drawer-item]');
    if (!item) return;

    var lineKey = input.getAttribute('data-drawer-update');
    var newQty = Math.max(0, parseInt(input.value, 10) || 0);

    item.classList.add('cart-drawer__item--loading');

    changeItem(lineKey, newQty)
      .then(applyCartUpdate)
      .catch(function (err) { handleCartError(item, err); });
  }

  // ─── Remove item ───

  function handleRemove(btn) {
    var lineKey = btn.getAttribute('data-drawer-remove');
    if (!lineKey) return;

    var item = btn.closest('[data-drawer-item]');
    if (item) {
      // Optimistically subtract this line's total from cart totals so the
      // footer doesn't show stale numbers during the 0.35s exit animation.
      var lineTotalEl = item.querySelector('.cart-drawer__item-line-total');
      if (lineTotalEl) {
        var lineTotal = parseMoney(lineTotalEl.textContent);
        var cartTotalEl = document.querySelector(SELECTORS.cartTotal);
        var cartFinalEl = document.querySelector(SELECTORS.cartFinal);
        if (cartTotalEl) {
          cartTotalEl.textContent = formatMoney(parseMoney(cartTotalEl.textContent) - lineTotal);
        }
        if (cartFinalEl) {
          cartFinalEl.textContent = formatMoney(parseMoney(cartFinalEl.textContent) - lineTotal);
        }
      }

      // If this is the last non-GWP line, hide the footer immediately so the
      // empty state can take over the moment the animation ends.
      var remainingItems = document.querySelectorAll('[data-drawer-item]').length;
      if (remainingItems <= 1) {
        var bottomEl = document.querySelector(SELECTORS.cartBottom);
        if (bottomEl) bottomEl.style.display = 'none';
      }

      item.classList.add('cart-drawer__item--exiting');
      // Wait for exit animation, then fire API call
      item.addEventListener('animationend', function onEnd() {
        item.removeEventListener('animationend', onEnd);
        changeItem(lineKey, 0)
          .then(applyCartUpdate)
          .catch(function (err) { handleCartError(item, err); });
      });
    } else {
      changeItem(lineKey, 0)
        .then(applyCartUpdate)
        .catch(function (err) { handleCartError(item, err); });
    }
  }

  // ─── Selling plan toggle ───

  function handleSellingPlanToggle(btn) {
    var lineIndex = parseInt(btn.getAttribute('data-line-index'), 10);
    var defaultPlanId = parseInt(btn.getAttribute('data-default-plan-id'), 10);
    var item = btn.closest('[data-drawer-item]');

    if (item) item.classList.add('cart-drawer__item--loading');

    var qtyInput = item ? item.querySelector(SELECTORS.quantityInput) : null;
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    changeItemByLine(lineIndex, qty, defaultPlanId)
      .then(applyCartUpdate)
      .catch(function (err) { handleCartError(item, err); });
  }

  function handleSellingPlanSelect(select) {
    var lineIndex = parseInt(select.getAttribute('data-line-index'), 10);
    var qty = parseInt(select.getAttribute('data-quantity'), 10) || 1;
    var planId = select.value ? parseInt(select.value, 10) : '';

    var item = select.closest('[data-drawer-item]');
    if (item) item.classList.add('cart-drawer__item--loading');

    changeItemByLine(lineIndex, qty, planId)
      .then(applyCartUpdate)
      .catch(function (err) { handleCartError(item, err); });
  }

  // ─── Product recommendations ───

  function showRecLoading(wrapper) {
    var skeleton = '<div class="cart-drawer__rec-shimmer">';
    skeleton += '<div class="cart-drawer__rec-shimmer-title"></div>';
    skeleton += '<div class="cart-drawer__rec-shimmer-grid">';
    for (var i = 0; i < 8; i++) {
      skeleton += '<div class="cart-drawer__rec-shimmer-card">';
      skeleton += '<div class="cart-drawer__rec-shimmer-img"></div>';
      skeleton += '<div class="cart-drawer__rec-shimmer-text"></div>';
      skeleton += '<div class="cart-drawer__rec-shimmer-text cart-drawer__rec-shimmer-text--short"></div>';
      skeleton += '</div>';
    }
    skeleton += '</div>';
    skeleton += '</div>';
    wrapper.innerHTML = skeleton;
  }

  function loadRecommendations() {
    var wrapper = document.querySelector(SELECTORS.recommendations);
    if (!wrapper) return;

    var productIdsEl = document.querySelector(SELECTORS.productIds);
    var cartProductIds = productIdsEl
      ? productIdsEl.getAttribute('data-cart-product-ids').split(',').filter(Boolean)
      : [];

    var fallbackId = wrapper.getAttribute('data-fallback-product-id');
    var firstProductId = cartProductIds.length > 0 ? cartProductIds[0] : fallbackId;

    if (!firstProductId) {
      wrapper.innerHTML = '';
      return;
    }

    showRecLoading(wrapper);

    fetch(theme.routes.product_recommendations_url + '.json?product_id=' + firstProductId + '&limit=10&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.products || data.products.length === 0) {
          wrapper.innerHTML = '';
          return;
        }

        var recs = data.products.filter(function (p) {
          return cartProductIds.indexOf(String(p.id)) === -1;
        }).slice(0, 8);

        if (recs.length === 0) {
          wrapper.innerHTML = '';
          return;
        }

        var html = '<h3 class="cart-drawer__recommendations-title">You May Also Like</h3>';
        html += '<div class="cart-drawer__recommendations-grid">';
        html += recs.map(function (product) {
          var imgSrc = product.featured_image ? product.featured_image.replace(/(\.[^.]+)$/, '_180x$1') : '';
          var price = formatMoney(product.price);

          var card = '<div class="cart-drawer__rec-card">';
          card += '<a href="' + product.url + '">';
          if (imgSrc) {
            card += '<img src="' + imgSrc + '" alt="' + escapeHtml(product.title) + '" loading="lazy">';
          }
          card += '<p class="cart-drawer__rec-card-title">' + escapeHtml(product.title) + '</p>';
          card += '<p class="cart-drawer__rec-card-price">' + price + '</p>';
          card += '</a>';
          card += '</div>';
          return card;
        }).join('');
        html += '</div>';

        wrapper.innerHTML = html;
      })
      .catch(function () {
        wrapper.innerHTML = '';
      });
  }

  // ─── Show loading shimmer for empty→filled transition ───

  function showDrawerLoading() {
    var emptyEl = document.querySelector(SELECTORS.cartEmpty);
    var formEl = document.querySelector(SELECTORS.cartForm);
    var bottomEl = document.querySelector(SELECTORS.cartBottom);
    var lineItems = document.querySelector(SELECTORS.lineItems);

    if (emptyEl) emptyEl.style.display = 'none';
    if (formEl) formEl.style.display = '';
    if (bottomEl) bottomEl.style.display = '';

    if (lineItems) {
      var shimmer = '<div class="cart-drawer__body-shimmer">';
      for (var i = 0; i < 2; i++) {
        shimmer += '<div class="cart-drawer__item-skeleton">';
        shimmer += '<div class="cart-drawer__item-skeleton-img"></div>';
        shimmer += '<div class="cart-drawer__item-skeleton-details">';
        shimmer += '<div class="cart-drawer__item-skeleton-line"></div>';
        shimmer += '<div class="cart-drawer__item-skeleton-line cart-drawer__item-skeleton-line--short"></div>';
        shimmer += '<div class="cart-drawer__item-skeleton-line cart-drawer__item-skeleton-line--price"></div>';
        shimmer += '</div>';
        shimmer += '<div class="cart-drawer__item-skeleton-total"></div>';
        shimmer += '</div>';
      }
      shimmer += '</div>';
      lineItems.innerHTML = shimmer;
    }
  }

  // ─── Auto-open drawer on add-to-cart ───

  function openDrawer() {
    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    var toggleBtn = document.querySelector(SELECTORS.drawerToggle);
    if (toggleBtn && !drawer.classList.contains('drawer--visible')) {
      toggleBtn.click();
    }
  }

  function isDrawerOpen() {
    var drawer = document.querySelector(SELECTORS.drawer);
    return drawer && drawer.classList.contains('drawer--visible');
  }

  // ─── Money formatting ───

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    var amount = (cents / 100).toFixed(2);
    var format = theme.moneyFormat || '{{amount}}';

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,'))
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function parseMoney(str) {
    if (!str) return 0;
    var cleaned = str.replace(/[^0-9.,]/g, '').replace(',', '.');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ─── Event delegation ───

  function handleDrawerClick(e) {
    var target = e.target;

    var decreaseBtn = target.closest(SELECTORS.decreaseBtn);
    if (decreaseBtn) {
      e.preventDefault();
      handleQuantityChange(decreaseBtn, -1);
      return;
    }

    var increaseBtn = target.closest(SELECTORS.increaseBtn);
    if (increaseBtn) {
      e.preventDefault();
      handleQuantityChange(increaseBtn, 1);
      return;
    }

    var removeBtn = target.closest(SELECTORS.removeBtn);
    if (removeBtn) {
      e.preventDefault();
      handleRemove(removeBtn);
      return;
    }

    var spToggle = target.closest(SELECTORS.sellingPlanToggle);
    if (spToggle) {
      e.preventDefault();
      handleSellingPlanToggle(spToggle);
      return;
    }

  }

  function handleDrawerChange(e) {
    var target = e.target;

    if (target.matches && target.matches(SELECTORS.quantityInput)) {
      handleQuantityInput(target);
      return;
    }

    if (target.matches && target.matches(SELECTORS.sellingPlanSelect)) {
      handleSellingPlanSelect(target);
      return;
    }
  }

  // ─── Intercept header cart links to open drawer ───

  function interceptCartLinks() {
    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest('a[href*="/cart"]');
      if (cartLink && !e.target.closest('.cart-drawer')) {
        var isHeaderCart = cartLink.closest('.header, .site-header, [data-section-type="header"]');
        if (isHeaderCart) {
          e.preventDefault();
          e.stopImmediatePropagation();
          openDrawer();
        }
      }
    }, true);
  }

  // ─── Initialise ───

  function interceptExternalCartMutations() {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      var isExternalCartMutation =
        !internalFetch &&
        (urlStr.indexOf('/cart/add') !== -1 ||
          urlStr.indexOf('/cart/change') !== -1 ||
          urlStr.indexOf('/cart/update') !== -1 ||
          urlStr.indexOf('/cart/clear') !== -1);

      if (!isExternalCartMutation) {
        return nativeFetch.apply(this, arguments);
      }

      // External app touched the cart — refresh the drawer once it completes
      return nativeFetch.apply(this, arguments).then(function (response) {
        if (response && response.ok) {
          // Small delay so other interceptors (e.g. GWP) can process first
          setTimeout(refreshCartDrawer, 150);
        }
        return response;
      });
    };
  }

  function getInitialItemCount() {
    var themeState = window.theme || {};
    return typeof themeState.cartItemCount === 'number' ? themeState.cartItemCount : null;
  }

  function consumePendingCart() {
    var pendingCart = window.__cartDrawerPendingCart;
    if (!pendingCart || typeof pendingCart.item_count !== 'number') return false;

    var shouldOpen = !!window.__cartDrawerShouldOpen && pendingCart.item_count > (lastItemCount || 0);
    window.__cartDrawerPendingCart = null;
    window.__cartDrawerShouldOpen = false;

    if (shouldOpen && lastItemCount === 0) {
      showDrawerLoading();
    }

    if (pendingCart.sections && pendingCart.sections[SECTION_KEY]) {
      applyCartUpdate(pendingCart);
    } else {
      toggleCartStates(pendingCart);
      updateHeaderBadge(pendingCart);
      lastItemCount = pendingCart.item_count;
      refreshCartDrawer();
    }

    if (shouldOpen && !isDrawerOpen()) {
      setTimeout(function () {
        openDrawer();
      }, 150);
    }

    return true;
  }

  function consumePendingOpenRequest() {
    if (!window.__cartDrawerShouldOpen) return false;

    window.__cartDrawerShouldOpen = false;
    if (lastItemCount === 0) {
      showDrawerLoading();
    }

    refreshCartDrawer();
    if (!isDrawerOpen()) {
      setTimeout(function () {
        openDrawer();
      }, 150);
    }

    return true;
  }

  function init() {
    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    interceptCartLinks();
    interceptExternalCartMutations();

    drawer.addEventListener('click', handleDrawerClick);
    drawer.addEventListener('change', handleDrawerChange);

    document.addEventListener('theme:cart:change', function (e) {
      if (!e.detail || !e.detail.cart) return;

      var cart = e.detail.cart;

      if (lastItemCount !== null && cart.item_count > lastItemCount && !isDrawerOpen()) {
        if (lastItemCount === 0) {
          showDrawerLoading();
        }
        setTimeout(function () {
          openDrawer();
        }, 150);
      }

      if (!e.detail._cartDrawerOrigin) {
        refreshCartDrawer();
        return;
      }

      toggleCartStates(cart);
      updateHeaderBadge(cart);
      lastItemCount = cart.item_count;
    });

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class') {
          if (drawer.classList.contains('drawer--visible')) {
            refreshCartDrawer();
          }
        }
      });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    var initialItemCount = getInitialItemCount();
    if (initialItemCount !== null) {
      lastItemCount = initialItemCount;
    }

    if (consumePendingCart()) return;
    if (consumePendingOpenRequest()) return;

    if (initialItemCount === null) {
      fetchCart().then(function (cart) {
        lastItemCount = cart.item_count;
        toggleCartStates(cart);
        updateHeaderBadge(cart);
      });
    }
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
